// Field — the form-field wrapper primitive, pure.
//
// Binds one <label> to one control and, when present, a hint and an error,
// with the a11y wiring done FOR the consumer so it cannot be forgotten
// (a11y is a floor, not an opt-in): the label via htmlFor, the hint and
// error via aria-describedby, and aria-invalid on the control while an
// error stands. The consumer passes `htmlFor` and the same id on the
// control; Field derives the describedby ids from it, so the two sides
// cannot drift out of sync.
//
// Generic by construction: it wraps ANY single control element (input,
// select, textarea, a design-system control) — it never imports Input or
// knows a control's shape beyond "accepts aria-describedby / aria-invalid".
// That contract is the exported `FieldControl` type, so the wiring is
// injected via cloneElement with no `as` cast and no untyped object.
//
// Pure/props-driven: no state, no I/O, no domain knowledge. Label, hint,
// and error are ReactNode the caller owns; the only string Field authors
// itself is the screen-reader "(required)" marker.
//
// Colour is never the sole channel (SC 1.4.1): the error is danger-coloured
// AND carries a warning glyph AND live-announces via role="alert"; the
// required marker is a glyph ("*") AND a screen-reader word, not a hue.

import { Trans } from "@lingui/react/macro";
import type { AriaAttributes, ReactElement, ReactNode } from "react";
import { cloneElement } from "react";
import { Icon } from "../Icon/Icon.js";
import "./Field.css";

// The minimal contract a wrapped control must satisfy: it accepts the two
// ARIA props Field injects. Any real form control does; taking exactly this
// slice (not `HTMLElement`) lets cloneElement type-check the injection
// without a cast.
export type FieldControl = Pick<
    AriaAttributes,
    "aria-describedby" | "aria-invalid"
>;

export type FieldProps = {
    readonly label: ReactNode;
    // Id shared between the <label> and the control the caller renders as
    // `children`; also the stem of the hint/error describedby ids.
    readonly htmlFor: string;
    readonly children: ReactElement<FieldControl>;
    readonly hint?: ReactNode;
    readonly error?: ReactNode;
    readonly required?: boolean;
};

// Warning triangle — the non-colour channel for the error (shape, not hue).
// Inherits currentColor (= --color-danger) and is decorative to AT.
const errorGlyph = <Icon name="warning" className="bilda-field__error-icon" />;

export const Field = ({
    label,
    htmlFor,
    children,
    hint,
    error,
    required,
}: FieldProps) => {
    const hintId = hint != null ? `${htmlFor}-hint` : undefined;
    const errorId = error != null ? `${htmlFor}-error` : undefined;
    const describedBy =
        [hintId, errorId]
            .filter((id): id is string => id !== undefined)
            .join(" ") || undefined;

    // Inject the wiring the control cannot know about itself. undefined for
    // either prop means "unset", so a field with neither hint nor error
    // stays clean.
    const control = cloneElement(children, {
        "aria-describedby": describedBy,
        "aria-invalid": error != null ? true : undefined,
    });

    return (
        <div className="bilda-field">
            <label htmlFor={htmlFor} className="bilda-field__label">
                {label}
                {required ? (
                    <span className="bilda-field__required">
                        <span aria-hidden="true">*</span>
                        <span className="bilda-field__sr-only">
                            <Trans>(required)</Trans>
                        </span>
                    </span>
                ) : null}
            </label>
            {control}
            {hint != null ? (
                <p id={hintId} className="bilda-field__hint">
                    {hint}
                </p>
            ) : null}
            {error != null ? (
                <p id={errorId} role="alert" className="bilda-field__error">
                    {errorGlyph}
                    <span>{error}</span>
                </p>
            ) : null}
        </div>
    );
};
